import React, { useState } from 'react';
import { Users, Home, Zap, Award, Network, Phone, Mail, Quote } from 'lucide-react';
import { stats } from '../data/mock';

const iconMap = {
  Users: Users,
  Home: Home,
  Zap: Zap,
  Award: Award,
  Network: Network
};

const teamMembers = [
  {
    id: 'admir',
    name: 'Admir Basic',
    role: 'Geschäftsführer',
    initials: 'AB',
    photoReal: 'https://customer-assets-cm19k8pv.emergentagent.net/job_a2b-facility/artifacts/f1qdxdu0_Adi_Sakko_dunklerHintergrund.webp',
    photoComic: 'https://customer-assets.emergentagent.com/job_a2b-facility/artifacts/eq8tgbv9_Admir_Comic_Beraterstyle.jpeg',
    quote:
      'Jede Immobilie ist Vertrauenssache. Ich empfehle nur, wem ich meine eigene anvertrauen würde – vom Handwerker bis zum Notar.',
    phone: '+49 151 51488980',
    phoneHref: 'tel:+4915151488980',
    email: 'admir@atob-objektservice.de',
    accent: 'from-atob-orange to-amber-500'
  }
];

const TeamCard = ({ member }) => {
  const [hovered, setHovered] = useState(false);
  const showComic = hovered && member.photoComic;

  return (
    <article
      data-testid={`team-card-${member.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`group relative bg-white/[0.03] backdrop-blur-sm border rounded-3xl p-7 md:p-8 transition-all duration-500 hover:bg-white/[0.06] hover:-translate-y-2 overflow-hidden flex flex-col h-full ${
        member.highlight
          ? 'border-atob-orange/60 shadow-[0_20px_50px_-20px_rgba(245,166,35,0.4)] hover:shadow-[0_30px_70px_-15px_rgba(245,166,35,0.55)]'
          : 'border-white/10 hover:border-atob-orange/40 hover:shadow-[0_25px_60px_-15px_rgba(245,166,35,0.35)]'
      }`}
    >
      {/* Highlight badge for first contact */}
      {member.highlight && (
        <div
          data-testid={`team-highlight-${member.id}`}
          className="absolute top-4 left-4 bg-atob-orange text-atob-blue text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-lg z-10"
        >
          ★ Direktkontakt
        </div>
      )}

      {/* Decorative gradient blob */}
      <div className={`absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gradient-to-br ${member.accent} opacity-10 blur-3xl group-hover:opacity-30 transition-opacity duration-700`}></div>

      {/* Quote icon watermark */}
      <Quote
        className="absolute top-6 right-6 h-10 w-10 text-atob-orange/15 group-hover:text-atob-orange/30 transition-colors duration-500"
        aria-hidden="true"
      />

      {/* Photo */}
      <div className="relative mb-6">
        <div className="relative w-40 h-40 md:w-44 md:h-44 mx-auto">
          {/* Orange ring */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-atob-orange to-amber-600 p-[3px] shadow-xl">
            <div className="w-full h-full rounded-full bg-atob-blue p-[3px]">
              <div className={`relative w-full h-full rounded-full overflow-hidden ${member.photoPending ? 'bg-white' : 'bg-atob-blue'}`}>
                {member.photoPending ? (
                  <>
                    <img
                      src={member.photoReal}
                      alt={`${member.name} – Foto folgt in Kürze`}
                      className="absolute inset-0 w-full h-full object-contain p-5"
                      loading="lazy"
                    />
                    {/* "Foto folgt" ribbon */}
                    <div className="absolute bottom-0 left-0 right-0 bg-atob-blue/90 backdrop-blur-sm py-1.5">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-atob-orange text-center">
                        Foto folgt
                      </p>
                    </div>
                  </>
                ) : member.photoReal ? (
                  <>
                    <img
                      src={member.photoReal}
                      alt={`${member.name} – ${member.role} bei AtoB Objektservice`}
                      className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                        showComic ? 'opacity-0 scale-110' : 'opacity-100 scale-100'
                      }`}
                      loading="lazy"
                    />
                    {member.photoComic && (
                      <img
                        src={member.photoComic}
                        alt={`${member.name} Illustration`}
                        className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                          showComic ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
                        }`}
                        aria-hidden="true"
                        loading="lazy"
                      />
                    )}
                  </>
                ) : (
                  <div
                    className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br ${member.accent} text-atob-blue font-black text-5xl md:text-6xl select-none`}
                    aria-label={`Initialen von ${member.name}`}
                  >
                    {member.initials}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Initials badge */}
          <div className="absolute -bottom-2 -right-2 md:-bottom-1 md:-right-1 bg-atob-orange text-atob-blue font-black text-sm md:text-base w-12 h-12 rounded-full flex items-center justify-center shadow-lg border-4 border-atob-blue">
            {member.initials}
          </div>
        </div>
      </div>

      {/* Name & Role */}
      <div className="text-center mb-5">
        <h3 className="text-2xl md:text-[1.6rem] font-bold text-white leading-tight mb-1.5 min-h-[3.5rem] flex items-center justify-center">
          {member.name}
        </h3>
        <p className="text-atob-orange font-semibold uppercase tracking-wider text-xs">
          {member.role}
        </p>
      </div>

      {/* Personal Quote */}
      <blockquote className="relative text-gray-200 italic text-[0.95rem] leading-relaxed text-center mb-6 px-2">
        <span className="text-atob-orange text-2xl leading-none align-top mr-1">“</span>
        {member.quote}
        <span className="text-atob-orange text-2xl leading-none align-bottom ml-1">”</span>
      </blockquote>

      {/* Signature line + Contact buttons pinned to bottom */}
      <div className="mt-auto pt-2">
        <div className="flex items-center gap-3 justify-center mb-6">
          <span className="h-px w-8 bg-atob-orange/50"></span>
          <span className="text-xs text-gray-400 font-medium tracking-widest uppercase">
            Persönlich für Sie da
          </span>
          <span className="h-px w-8 bg-atob-orange/50"></span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <a
            href={member.phoneHref}
            data-testid={`team-phone-${member.id}`}
            className="flex items-center justify-center gap-2 bg-atob-orange hover:bg-amber-500 text-atob-blue font-semibold py-3 px-3 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-atob-orange/30 hover:-translate-y-0.5 text-sm"
          >
            <Phone className="h-4 w-4" />
            <span>Anrufen</span>
          </a>
          <a
            href={`mailto:${member.email}?cc=info@atob-objektservice.de&subject=Anfrage%20an%20${encodeURIComponent(member.name)}`}
            data-testid={`team-email-${member.id}`}
            className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 hover:border-atob-orange/50 text-white font-semibold py-3 px-3 rounded-xl transition-all duration-300 hover:-translate-y-0.5 text-sm"
          >
            <Mail className="h-4 w-4" />
            <span>E-Mail</span>
          </a>
        </div>
      </div>
    </article>
  );
};

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-28 bg-atob-blue overflow-hidden"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-atob-orange/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-atob-orange/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header — Dittmeyer style */}
        <div className="max-w-4xl mx-auto text-center mb-16 md:mb-20">
          <span className="inline-flex items-center gap-2 text-atob-orange font-semibold text-sm uppercase tracking-[0.2em] mb-5">
            <span className="h-px w-8 bg-atob-orange"></span>
            Der Kopf hinter AtoB
            <span className="h-px w-8 bg-atob-orange"></span>
          </span>
          <h2
            data-testid="about-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] mb-7"
          >
            Dafür stehe ich{' '}
            <span className="block text-atob-orange italic font-serif mt-2">
              mit meinem Namen.
            </span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Hinter AtoB steht kein anonymes Callcenter, sondern{' '}
            <span className="text-white font-semibold">der Geschäftsführer persönlich</span> –
            aufgewachsen in der Region, seit Jahren im Immobilien-Geschäft, mit einem
            Netzwerk, das nicht am Reißbrett entstand, sondern über Jahre persönlicher
            Zusammenarbeit gewachsen ist.
          </p>
        </div>

        {/* Team Grid */}
        <div
          data-testid="team-grid"
          className="grid grid-cols-1 gap-8 max-w-md mx-auto mb-20 md:mb-24"
        >
          {teamMembers.map((member) => (
            <TeamCard key={member.id} member={member} />
          ))}
        </div>

        {/* Personal guarantee strip */}
        <div className="max-w-4xl mx-auto mb-20 md:mb-24">
          <div className="relative bg-gradient-to-r from-atob-orange via-amber-500 to-atob-orange rounded-2xl p-8 md:p-10 shadow-2xl overflow-hidden">
            <div className="absolute inset-0 opacity-10 mix-blend-overlay" style={{
              backgroundImage:
                'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 50%, white 1px, transparent 1px)',
              backgroundSize: '40px 40px'
            }}></div>
            <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <div className="text-6xl md:text-7xl font-black text-atob-blue leading-none flex-shrink-0">
                100<span className="text-3xl">%</span>
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-atob-blue mb-2">
                  Unser Versprechen an Sie
                </h3>
                <p className="text-atob-blue/90 text-base md:text-lg leading-relaxed">
                  Ehrliche Empfehlung, unabhängige Beratung, transparente Kosten. Wenn ein Partner
                  aus unserem Netzwerk nicht liefert, wechseln wir – ohne Diskussion.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div
          data-testid="about-stats"
          className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 max-w-6xl mx-auto"
        >
          {stats.map((stat) => {
            const Icon = iconMap[stat.icon];
            return (
              <div key={stat.id} className="text-center group">
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 bg-atob-orange/15 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:bg-atob-orange/25 group-hover:scale-110 transition-all duration-300">
                    <Icon className="h-7 w-7 text-atob-orange" />
                  </div>
                </div>
                <p className="text-3xl md:text-5xl font-black text-white mb-1.5">{stat.value}</p>
                <p className="text-sm md:text-base text-gray-300">{stat.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
